import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { v, assertValid } from '@/utils/validateStore'

const webSocketEventSchema = v.shape({
  event: v.string,
  channel: v.optional(v.string),
  data: v.optional(v.shape({})),
  timestamp: v.string,
})

export interface WebSocketEvent {
  event: string
  channel?: string
  data: any
  timestamp: string
}

export interface CallStartedEvent {
  operator_id: number
  lead_id: number | null
  phone_number: string
  campaign_id: number | null
}

export interface CallEndedEvent {
  operator_id: number
  lead_id?: number | null
  phone_call_id: number
  duration: number | null
  result: string | null
  campaign_id: number | null
  operator_name?: string | null
  total_calls_today?: number
  sales_today?: number
}

export interface OperatorStatusChangedEvent {
  operator_id: number
  status: string
  campaign_id: number | null
  operator_name?: string | null
  total_calls_today?: number
  sales_today?: number
  avg_call_duration?: number
  status_color?: string | null
}

export interface CampaignStatsUpdatedEvent {
  campaign_id: number
  stats: {
    total_leads?: number
    leads_called?: number
    leads_pending?: number
    conversion_rate?: number
    progress?: number
    lead_sources?: string[]
    [key: string]: any
  }
}

export interface NewLeadEvent {
  lead_id: number
  lead_number: string
  full_name: string
  phone: string
  campaign_id: number | null
  priority: string
  lead_source: string | null
  generator_id?: number | null
  assigned_operator_id?: number | null
}

export interface LeadUpdatedEvent {
  lead_id: number
  status: string
  campaign_id: number | null
  conversion_status?: string | null
  assigned_operator_id?: number | null
  sale_amount?: number | null
  generator_id?: number | null
}

export interface RecordingCreatedEvent {
  recording_id: number
  phone_call_id: number
  recording_url: string
  lead_id?: number | null
  operator_id?: number | null
  operator_name?: string | null
  full_name?: string | null
  campaign_id?: number | null
  campaign_name?: string | null
  duration_seconds?: number | null
  started_at?: string
}

export interface RecordingUpdatedEvent {
  recording_id: number
  phone_call_id: number
  listened?: boolean
  whispered?: boolean
  listened_at?: string | null
  whispered_at?: string | null
}

interface QueuedEvent {
  id: number
  event: WebSocketEvent
}

const MAX_QUEUED_EVENTS = 100

function normalizePayload(event: string, data: any): any {
  // Mantener una API estable para los consumidores aunque el backend cambie
  // ligeramente la forma del payload.
  if (event === 'campaign_stats_updated' && data && typeof data === 'object' && !('stats' in data)) {
    const { campaign_id, ...stats } = data
    return { campaign_id, stats }
  }
  if (event === 'recording_updated' && data && typeof data === 'object' && !('phone_call_id' in data)) {
    return {
      phone_call_id: data.recording_id,
      recording_id: data.recording_id,
      listened: data.listened,
      whispered: data.whispered,
      listened_at: data.listened_at,
      whispered_at: data.whispered_at,
    }
  }
  return data
}

export const useWebSocketStore = defineStore('websocket', () => {
  const ws = ref<WebSocket | null>(null)
  const connectionStatus = ref<'connecting' | 'connected' | 'disconnected' | 'error'>('disconnected')
  const reconnectAttempts = ref(0)
  const maxReconnectAttempts = 5
  const reconnectDelay = ref(1000)

  const events = ref<QueuedEvent[]>([])
  const eventCounter = ref(0)

  // lastEvent se mantiene por compatibilidad con consumidores legados.
  const lastEvent = ref<WebSocketEvent | null>(null)
  const subscribedChannels = ref<Set<string>>(new Set())

  const isConnected = computed(() => connectionStatus.value === 'connected')
  const isConnecting = computed(() => connectionStatus.value === 'connecting')

  let reconnectTimeout: number | null = null

  const connect = () => {
    if (ws.value?.readyState === WebSocket.OPEN) return
    if (ws.value?.readyState === WebSocket.CONNECTING) return

    if (ws.value) {
      ws.value.onclose = null
      ws.value.onerror = null
      ws.value.close()
    }

    connectionStatus.value = 'connecting'

    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:'
    const host = window.location.host
    const wsUrl = import.meta.env.VITE_WS_URL || `${protocol}//${host}/ws`

    const socket = new WebSocket(wsUrl)
    ws.value = socket

    socket.onopen = () => {
      connectionStatus.value = 'connected'
      reconnectAttempts.value = 0
      reconnectDelay.value = 1000

      subscribedChannels.value.forEach(channel => {
        if (socket.readyState === WebSocket.OPEN) {
          socket.send(JSON.stringify({ command: 'subscribe', channel }))
        }
      })

      console.log('[WebSocket] Connected')
    }

    socket.onmessage = (event) => {
      try {
        const message = JSON.parse(event.data)
        assertValid('websocket.onmessage', message, webSocketEventSchema)

        const normalized = {
          event: message.event,
          channel: message.channel ?? undefined,
          data: normalizePayload(message.event, message.data),
          timestamp: message.timestamp ?? '',
        }

        const id = ++eventCounter.value
        events.value.push({ id, event: normalized })
        if (events.value.length > MAX_QUEUED_EVENTS) {
          events.value.shift()
        }

        lastEvent.value = normalized

        if (message.event === 'pong' || message.event === 'subscribed' || message.event === 'unsubscribed') {
          return
        }

        console.log('[WebSocket] Event:', message.event, normalized.data)
      } catch (error) {
        console.error('[WebSocket] Parse error:', error)
      }
    }

    socket.onerror = (error) => {
      console.error('[WebSocket] Error:', error)
      connectionStatus.value = 'error'
    }

    socket.onclose = () => {
      if (ws.value === socket) {
        connectionStatus.value = 'disconnected'
        console.log('[WebSocket] Disconnected')
        attemptReconnect()
      }
    }
  }

  const disconnect = () => {
    if (reconnectTimeout) {
      clearTimeout(reconnectTimeout)
      reconnectTimeout = null
    }

    if (ws.value) {
      ws.value.close()
      ws.value = null
    }

    connectionStatus.value = 'disconnected'
  }

  const attemptReconnect = () => {
    if (reconnectAttempts.value >= maxReconnectAttempts) {
      console.error('[WebSocket] Max reconnect attempts reached')
      connectionStatus.value = 'error'
      return
    }

    reconnectAttempts.value++
    const delay = reconnectDelay.value * Math.pow(2, reconnectAttempts.value - 1)

    console.log(`[WebSocket] Reconnecting in ${delay}ms (attempt ${reconnectAttempts.value})`)

    reconnectTimeout = window.setTimeout(() => {
      connect()
    }, delay)
  }

  const send = (message: any) => {
    if (ws.value?.readyState === WebSocket.OPEN) {
      ws.value.send(JSON.stringify(message))
    } else {
      console.warn('[WebSocket] Cannot send, not connected')
    }
  }

  const subscribe = (channel: string) => {
    subscribedChannels.value.add(channel)

    if (connectionStatus.value === 'connected') {
      send({ command: 'subscribe', channel })
    }
  }

  const unsubscribe = (channel: string) => {
    subscribedChannels.value.delete(channel)

    if (connectionStatus.value === 'connected') {
      send({ command: 'unsubscribe', channel })
    }
  }

  const ping = () => {
    send({ command: 'ping' })
  }

  const resetReconnectAttempts = () => {
    reconnectAttempts.value = 0
    reconnectDelay.value = 1000
  }

  return {
    ws,
    connectionStatus,
    reconnectAttempts,
    events,
    eventCounter,
    lastEvent,
    subscribedChannels,
    isConnected,
    isConnecting,
    connect,
    disconnect,
    send,
    subscribe,
    unsubscribe,
    ping,
    resetReconnectAttempts,
  }
})
